import { orgUnitTypeSchema, type OrgUnitType } from '@eombor/contracts';
import { cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';

/**
 * Tur kodlari SHARTNOMADAN olinadi, qo'lda ko'chirilmaydi.
 *
 * Ilgari bu ro'yxat `OrgTree.tsx` da `KNOWN_TYPES` bo'lib takrorlangan edi.
 * Shartnomaga yangi tur qo'shilganda daraxt uni "noma'lum" deb kod holida
 * chizardi va buni birorta sinov ushlamasdi — nusxa jimgina eskirardi.
 */
const KNOWN: readonly string[] = orgUnitTypeSchema.options;

/** Noma'lum tur kod holida qoladi — kalit o'rniga tushunarsiz matn chiqmaydi. */
export const orgTypeLabel = (type: string): string =>
  KNOWN.includes(type) ? t(`org.type${type}`) : type;

/**
 * Turlarning BESH oilasi.
 *
 * Daraxtda 17 xil tur bor va ularning har biriga alohida rang berilsa,
 * ro'yxat rangli shovqinga aylanadi. Shu sababli ular DARAJASI bo'yicha
 * guruhlanadi: MTU — ildiz, stansiya — oxirgi bo'g'in, yo'nalish/uchastka —
 * oraliq bo'g'inlar, qolgan o'n ikkitasi — korxonalar.
 *
 * `OTHER` ataylab korxonalardan AJRALIB turadi: u "turi noma'lum" degani
 * va uni PCh yoki TCh bilan bir xil ko'rsatish — bilmagan narsani bilgandek
 * qilib ko'rsatish.
 */
type Family = 'root' | 'station' | 'link' | 'unit' | 'unknown';

const familyOf = (type: string): Family => {
  if (type === 'MTU') return 'root';
  if (type === 'STATION') return 'station';
  if (type === 'LINE' || type === 'DIVISION') return 'link';
  if (type === 'OTHER' || !KNOWN.includes(type)) return 'unknown';
  return 'unit';
};

/*
 * Fon TOKENDAN olinadi, asosiy rangning 12% tiniqligidan emas.
 *
 * Spetsifikatsiya 12% tint so'ragan edi, lekin o'lchov uni rad etdi: 11px
 * yozuv uchun WCAG 4.5:1 talab qiladi, `--success` ning 12% tinti esa oq
 * qatorda 4.35:1, tanlangan (`--accent-soft`) qatorda esa 3.82:1 beradi —
 * chunki yarim shaffof fon ostidagi rangga qarab o'zgaradi. Loyihadagi
 * `*-soft` tokenlari xuddi shu maqsad uchun bor, ular XIRA EMAS va ikkala
 * temada ham tekshirilgan (eng pasti 4.55:1).
 */
const familyClass: Record<Family, string> = {
  root: 'bg-accent-soft text-accent',
  station: 'bg-success-soft text-success',
  link: 'bg-warn-soft text-warn',
  unit: 'bg-primary-soft text-primary-300',
  unknown: 'bg-surface-2 text-text-muted',
};

export interface TypeBadgeProps {
  type: OrgUnitType | string;
  className?: string;
}

/**
 * Tuzilma tugunining TUR belgisi.
 *
 * Balandligi SOBIT 20px: daraxtda nishonlar ustma-ust turadi va kontentga
 * qarab suzadigan balandlik ularni notekis qilib ko'rsatardi. `shrink-0` —
 * nishon hech qachon siqilmaydi, aks holda u nom ustiga chiqib ketardi.
 */
export const TypeBadge = ({ type, className }: TypeBadgeProps) => (
  <span
    className={cn(
      'inline-flex h-5 shrink-0 items-center rounded-[var(--radius-pill)]',
      'px-2 text-[11px] leading-none font-medium whitespace-nowrap',
      familyClass[familyOf(type)],
      className,
    )}
  >
    {orgTypeLabel(type)}
  </span>
);
